import React, { useEffect, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View, TextInput } from 'react-native';
import { DataStore, SortDirection } from 'aws-amplify';
import { AppUser, UserType } from '../../models';
import { MaterialIcons } from '@expo/vector-icons';
import { DataTable } from 'react-native-paper';
import * as DataStoreUtil from '../../components/DataStoreUtil';
import * as AppConst from '../../components/AppConst';
import AddUser from './AddUser';
import UserDetail from './UserDetail';
import UpdateUser from './UpdateUser';

/** ================================
*  ユーザー管理画面
*  ================================*/
const UserList = () => {
  interface ManagedUser {
    id: string;
    groupname: string;
    username: string;
    authUserID: string;
    type: UserType | keyof typeof UserType;
    isEnabled?: boolean | null;
  }

  // 全ユーザーリスト
  const [userList, setUserList] = useState<ManagedUser[]>([]);
  // 表示ユーザーリスト
  const [userListPage, setUserPage] = useState<ManagedUser[]>([]);

  // 変更対象ユーザー
  const [targetUser, setTargetUser] = useState<string>('');

  // 各種ダイアログ表示フラグ
  const [addUser, setAddUser] = useState<boolean>(false);
  const [detail, setDetail] = useState<boolean>(false);
  const [update, setUpdate] = useState<boolean>(false);

  // ページング
  const numberOfItemsPerPage = 10;
  const [page, setPage] = useState(0);

  // 検索ワード
  const [findWord, setFindWord] = useState('');

  useEffect(() => {
    // 最新情報取得
    updateAppUser();

    // 購読開始
    const observeAppUser = DataStore.observe(AppUser).subscribe(updateAppUser);

    // 画面破棄時に購読終了
    return () => {
      observeAppUser.unsubscribe();
    }
  }, []);

  const updateAppUser = async () => {
    const users = (await DataStore.query(
      AppUser,
      c => c.or(c => c.type('eq', UserType.PRODUCER).type('eq', UserType.INSTRUCTOR)).or(c => c.username('contains', findWord).authUserID('contains', findWord)),
      {
        sort: s => s.createdAt(SortDirection.ASCENDING),
      }
    ));

    const tmpManagementUser: ManagedUser[] = [];
    for (const user of users) {
      tmpManagementUser.push({ id: user.id, groupname: user.group.groupName, username: user.username, authUserID: user.authUserID, type: user.type, isEnabled: user.isEnabled });
    }
    setUserList([...tmpManagementUser]);
  }

  // ページ切り替え時に表示切り替え
  useEffect(() => {
    setUserPage([...(userList.slice(page * numberOfItemsPerPage, (page + 1) * numberOfItemsPerPage))]);
  }, [page, userList]);

  return (
    <View style={styles.container}>
      <View style={{ margin: 10 }}>
        {/* 新規登録 */}
        <TouchableOpacity style={styles.addUser} onPress={() => { setAddUser(true); }}>
          <Text style={{ color: AppConst.defaultTextColor }}>＋ 新規登録</Text>
        </TouchableOpacity>

        {/* 検索窓 */}
        <View style={{ width: '100%', flexDirection: 'row', justifyContent: 'flex-end', marginTop: 30 }}>
          <TextInput
            style={{ borderBottomWidth: 1, width: 400, paddingLeft: 5 }}
            value={findWord}
            onChangeText={setFindWord}
            textContentType='none'
            autoCapitalize='none'
            placeholder='ユーザー名 or メールアドレスで検索してリスト表示'
          />
          <TouchableOpacity style={{ borderBottomWidth: 1, width: 50, alignItems: 'center' }} onPress={() => { setPage(0); updateAppUser(); }}>
            <MaterialIcons name='search' color={AppConst.defaultColor} size={24} />
          </TouchableOpacity>
        </View>

        {/* ユーザー一覧 */}
        <DataTable style={{ marginTop: 10 }}>
          <DataTable.Header style={{}}>
            <DataTable.Title style={{ flex: 1 }}>ID</DataTable.Title>
            <DataTable.Title style={{ flex: 2, paddingLeft: 20 }}>組織</DataTable.Title>
            <DataTable.Title style={{ flex: 2, paddingLeft: 20 }}>ユーザー名</DataTable.Title>
            <DataTable.Title style={{ flex: 2, paddingLeft: 20 }}>メールアドレス</DataTable.Title>
            <DataTable.Title style={{ flex: 1, paddingLeft: 20 }}>タイプ</DataTable.Title>
            <DataTable.Title style={{ flex: 1, paddingLeft: 20 }}>状態</DataTable.Title>
            <DataTable.Title style={{ flex: 1, paddingLeft: 20 }}>操作</DataTable.Title>
          </DataTable.Header>

          {userListPage.map((data: ManagedUser, index: number) => (
            <DataTable.Row key={index} style={[{ minHeight: 35 }, index % 2 == 0 ? styles.lineEven : styles.lineOdd]}
              onPress={() => { setTargetUser(data.id); setDetail(true); }}>
              <DataTable.Cell style={{ flex: 1 }}><Text style={AppConst.styles.fontSizeS}>{data.id}</Text></DataTable.Cell>
              <DataTable.Cell style={{ flex: 2, paddingLeft: 20 }}>{data.groupname}</DataTable.Cell>
              <DataTable.Cell style={{ flex: 2, paddingLeft: 20 }}>{data.username}</DataTable.Cell>
              <DataTable.Cell style={{ flex: 2, paddingLeft: 20 }}>{data.authUserID}</DataTable.Cell>
              <DataTable.Cell style={{ flex: 1, paddingLeft: 20 }}>{DataStoreUtil.convUserType(data.type)}</DataTable.Cell>
              <DataTable.Cell style={{ flex: 1, paddingLeft: 20 }}>{DataStoreUtil.convIsEnabled(data.isEnabled)}</DataTable.Cell>
              <DataTable.Cell style={{ flex: 1, paddingLeft: 20 }} onPress={() => { setTargetUser(data.id); setUpdate(true); }}>
                <Text style={styles.userControl}>アカウント操作</Text>
              </DataTable.Cell>
            </DataTable.Row>
          ))}

          <DataTable.Pagination
            page={page}
            numberOfPages={Math.ceil(userList.length / numberOfItemsPerPage)}
            onPageChange={(page) => setPage(page)}
            label={(page + 1) + 'ページ' + ' / ' + Math.ceil(userList.length / numberOfItemsPerPage) + 'ページ'}
            showFastPaginationControls={true}
            numberOfItemsPerPage={numberOfItemsPerPage}
          />
        </DataTable>
      </View>

      {/* ユーザー登録 */}
      {addUser && <AddUser closeFunction={() => { setAddUser(false) }} />}

      {/* ユーザー詳細 */}
      {detail && <UserDetail closeFunction={() => { setDetail(false); setTargetUser(''); }} userId={targetUser} />}

      {/* アカウント操作 */}
      {update && <UpdateUser closeFunction={() => { setUpdate(false); setTargetUser(''); }} userId={targetUser} />}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  addUser: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: AppConst.defaultColor,
    height: 30,
    width: '100%',
  },
  lineOdd: {
    backgroundColor: '#f4f4f4',
  },
  lineEven: {
    backgroundColor: '#f4ffff',
  },
  userControl: {
    textDecorationLine: 'underline',
    color: '#4b0082',
  },
});

export default UserList;
